import React, { useMemo, useState } from 'react';
import { Table, DatePicker, Button } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  DATETIME_FORMAT_DATE,
  formattedMorphUserName,
  friEduGetEnrollDisabledReason,
  friEduGetUserParticipantId,
  friEduIsUserEnrolled,
} from '../../../../../../utils/helpers/CommonHelper.jsx';
import {
  UserAddOutlined,
  UserDeleteOutlined,
  FileSearchOutlined,
} from '@ant-design/icons';
import { IconButton } from '../../../../../common/controls/Controls.jsx';
import { QuestionModal } from '../../../../../common/index.jsx';

const MyEduCourseDatesTable = ({
  datesData = [],
  isLoggedIn,
  hasParticipantRole,
  onEnroll,
  onUnenroll,
  onRequireAuth,
  onShowCourseDetail,
}) => {
  const { t } = useTranslation();
  const dataSource = Array.isArray(datesData) ? datesData : [];

  // Derive filter options
  const courseFilterOptions = useMemo(() => {
    const names = new Set();
    dataSource.forEach((r) => {
      if (r?.edu_course?.name) names.add(r.edu_course.name);
    });
    return Array.from(names).map((n) => ({ text: n, value: n }));
  }, [dataSource]);

  const lecturerFilterOptions = useMemo(() => {
    const names = new Set();
    dataSource.forEach((r) => {
      const n = formattedMorphUserName(r?.edu_course?.lecturerable);
      if (n) names.add(n);
    });
    return Array.from(names).map((n) => ({ text: n, value: n }));
  }, [dataSource]);

  const placeFilterOptions = useMemo(() => {
    const places = new Set();
    dataSource.forEach((r) => {
      if (r?.place) places.add(r.place);
    });
    return Array.from(places).map((p) => ({ text: p, value: p }));
  }, [dataSource]);

  // Local state for confirm modal
  const [pendingUnenroll, setPendingUnenroll] = useState(null); // { dateId, participantId }

  const serializeRange = (range) => {
    if (!Array.isArray(range) || range.length !== 2) return '';
    const [from, to] = range;
    return `${from ? from.toISOString() : ''}|${to ? to.toISOString() : ''}`;
  };
  const parseRange = (value) => {
    if (!value || typeof value !== 'string') return { from: null, to: null };
    const [fromStr, toStr] = value.split('|');
    const from = fromStr ? dayjs(fromStr) : null;
    const to = toStr ? dayjs(toStr) : null;
    return { from, to };
  };

  const columns = useMemo(
    () => [
      {
        title: t('Kurz'),
        dataIndex: ['edu_course', 'name'],
        key: 'course',
        ellipsis: true,
        filters: courseFilterOptions,
        onFilter: (value, record) => (record?.edu_course?.name || '') === value,
        sorter: (a, b) =>
          (a?.edu_course?.name || '').localeCompare(b?.edu_course?.name || ''),
      },
      {
        title: t('Hlavný lektor'),
        key: 'lecturer',
        render: (_, row) =>
          formattedMorphUserName(row?.edu_course?.lecturerable) || '',
        ellipsis: true,
        filters: lecturerFilterOptions,
        onFilter: (value, record) =>
          formattedMorphUserName(record?.edu_course?.lecturerable) === value,
        sorter: (a, b) =>
          formattedMorphUserName(a?.edu_course?.lecturerable).localeCompare(
            formattedMorphUserName(b?.edu_course?.lecturerable),
          ),
      },
      {
        title: t('Začiatok'),
        dataIndex: 'date_from',
        key: 'date_from',
        render: (v) => (v ? dayjs(v).format(DATETIME_FORMAT_DATE) : ''),
        sorter: (a, b) =>
          new Date(a?.date_from || 0) - new Date(b?.date_from || 0),
        defaultSortOrder: 'ascend',
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }) => {
          const value = selectedKeys?.[0];
          const { from, to } = parseRange(value);
          return (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
              <DatePicker.RangePicker
                value={[from, to]}
                onChange={(range) =>
                  setSelectedKeys(range ? [serializeRange(range)] : [])
                }
                format={DATETIME_FORMAT_DATE}
              />
              <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                <Button type="primary" size="small" onClick={() => confirm()}>
                  {t('Filtrovať')}
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    clearFilters?.();
                    confirm();
                  }}
                >
                  {t('Zrušiť')}
                </Button>
              </div>
            </div>
          );
        },
        onFilter: (value, record) => {
          const { from, to } = parseRange(value);
          if (!from && !to) return true;
          const d = record?.date_from ? dayjs(record.date_from) : null;
          if (!d) return false;
          const geFrom = from
            ? d.isSame(from, 'day') || d.isAfter(from, 'day')
            : true;
          const leTo = to ? d.isSame(to, 'day') || d.isBefore(to, 'day') : true;
          return geFrom && leTo;
        },
      },
      {
        title: t('Koniec'),
        dataIndex: 'date_to',
        key: 'date_to',
        render: (v) => (v ? dayjs(v).format(DATETIME_FORMAT_DATE) : ''),
        sorter: (a, b) => new Date(a?.date_to || 0) - new Date(b?.date_to || 0),
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }) => {
          const value = selectedKeys?.[0];
          const { from, to } = parseRange(value);
          return (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
              <DatePicker.RangePicker
                value={[from, to]}
                onChange={(range) =>
                  setSelectedKeys(range ? [serializeRange(range)] : [])
                }
                format={DATETIME_FORMAT_DATE}
              />
              <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                <Button type="primary" size="small" onClick={() => confirm()}>
                  {t('Filtrovať')}
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    clearFilters?.();
                    confirm();
                  }}
                >
                  {t('Zrušiť')}
                </Button>
              </div>
            </div>
          );
        },
        onFilter: (value, record) => {
          const { from, to } = parseRange(value);
          if (!from && !to) return true;
          const d = record?.date_to ? dayjs(record.date_to) : null;
          if (!d) return false;
          const geFrom = from
            ? d.isSame(from, 'day') || d.isAfter(from, 'day')
            : true;
          const leTo = to ? d.isSame(to, 'day') || d.isBefore(to, 'day') : true;
          return geFrom && leTo;
        },
      },
      {
        title: t('Miesto'),
        dataIndex: 'place',
        key: 'place',
        ellipsis: true,
        filters: placeFilterOptions,
        onFilter: (value, record) => (record?.place || '') === value,
        sorter: (a, b) => (a?.place || '').localeCompare(b?.place || ''),
      },
      {
        title: t('Akcie'),
        key: 'actions',
        width: 200,
        render: (_, row) => {
          const enrolled = isLoggedIn && friEduIsUserEnrolled(row);
          const detailBtn = (
            <IconButton
              icon={<FileSearchOutlined />}
              tooltip={t('Zobraziť detail kurzu')}
              onClick={(e) => {
                e.stopPropagation();
                const courseId = row?.edu_course?.id;
                if (courseId) onShowCourseDetail?.(courseId);
              }}
            />
          );

          if (enrolled) {
            const participantId = friEduGetUserParticipantId(row);
            return (
              <div className="d-flex">
                <IconButton
                  icon={<UserDeleteOutlined />}
                  tooltip={t('Odhlásiť sa z termínu')}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (participantId)
                      setPendingUnenroll({ dateId: row.id, participantId });
                  }}
                />
                {detailBtn}
              </div>
            );
          }
          const disabledReason = friEduGetEnrollDisabledReason(row, t);
          const disabled = !!disabledReason || !hasParticipantRole;
          return (
            <div className="d-flex">
              <IconButton
                icon={<UserAddOutlined />}
                tooltip={t('Prihlásiť sa na termín')}
                disabled={disabled}
                disabledTooltip={
                  disabled
                    ? disabledReason ||
                      t('Nemáte oprávnenie prihlásiť sa na termín')
                    : undefined
                }
                onClick={(e) => {
                  e.stopPropagation();
                  if (disabled) return;
                  if (!isLoggedIn) return onRequireAuth?.(row);
                  onEnroll?.(row.id);
                }}
              />
              {detailBtn}
            </div>
          );
        },
      },
    ],
    [
      t,
      isLoggedIn,
      hasParticipantRole,
      onShowCourseDetail,
      onEnroll,
      onRequireAuth,
      courseFilterOptions,
      lecturerFilterOptions,
      placeFilterOptions,
    ],
  );

  return (
    <>
      <Table
        rowKey={(r) => r.id}
        columns={columns}
        dataSource={dataSource}
        pagination={{ pageSize: 10, placement: ['bottomRight'] }}
      />

      <QuestionModal
        visible={!!pendingUnenroll}
        toggle={() => setPendingUnenroll(null)}
        chosenPositive={() => {
          if (!pendingUnenroll) return;
          onUnenroll?.(pendingUnenroll);
          setPendingUnenroll(null);
        }}
        headerTitle={t('Odhlásiť sa z termínu')}
        question={t('Naozaj chcete odhlásiť sa z tohto termínu?')}
        yes={t('Áno')}
      />
    </>
  );
};

export default MyEduCourseDatesTable;
